import {
    fetchFundRequestItemPresignedUrl,
    uploadFile,
    saveFundRequestItemImage,
    fetchFundRequestDetail,
    updateFundRequest
} from './fund-requests';
import { SelectOption } from '~/types/globals';
import { DefaultExpenseItem, FundRequest, TransactionFood, TransactionTravel, Vendor } from '~/types/fundrequests';

// Define the PendingUpload type here instead of importing it
export interface PendingUpload {
    file: File;
    originalName: string;
    size: number;
    type: string;
    preview: string;
}

/**
 * Determines the expense item type for the upload path based on transaction type
 */
export const getExpenseItemType = (transactionType: string): string | null => {
    if (!transactionType) return null;

    if (transactionType === 'transactions_travel') {
        return 'travel';
    } else if (transactionType === 'transactions_food') {
        return 'food';
    } else if (transactionType.startsWith('transactions_')) {
        return 'default';
    }

    return null;
};

/**
 * Combine default type transactions from multiple categories
 */
export const combineDefaultTransactions = (data: any) => {
    const transactions_default = [
        data.transactions_fundraising || [],
        data.transactions_medical || [],
        data.transactions_medications || [],
        data.transactions_insurance || [],
        data.transactions_caregiving || [],
        data.transactions_physical_therapy || [],
        data.transactions_relocation_lodging || [],
        data.transactions_other || [],
    ].flat();

    // Create a copy of the data without the transaction fields
    const newData = { ...data };
    delete newData.transactions_fundraising;
    delete newData.transactions_medical;
    delete newData.transactions_medications;
    delete newData.transactions_insurance;
    delete newData.transactions_caregiving;
    delete newData.transactions_physical_therapy;
    delete newData.transactions_relocation_lodging;
    delete newData.transactions_other;

    return {
        processedData: newData,
        transactions_default
    };
};

/**
 * Find the current item in the response based on the formKey
 */
export const findCurrentItem = (resp: any, formKey: string | null, categories: SelectOption[]) => {

    if (!formKey) return { currentItem: null, currentType: null };

    let currentItem;
    let currentType;

    // Extract info from the formKey (e.g., "transactions_medical[0]")
    const formKeyParts = formKey.match(/^([^[]+)\[(\d+)\]/);

    if (!formKeyParts) return { currentItem: null, currentType: null };

    const [, transactionType, indexStr] = formKeyParts;
    currentType = transactionType;
    const index = parseInt(indexStr, 10);

    if (!resp || typeof resp !== 'object') {
        console.error('Invalid response object:', resp);
        return { currentItem: null, currentType: null };
    }

    // Find the corresponding item in the response
    if (transactionType === 'transactions_travel' && Array.isArray(resp.transactions_travel)) {
        currentItem = resp.transactions_travel[index];
    } else if (transactionType === 'transactions_food' && Array.isArray(resp.transactions_food)) {
        currentItem = resp.transactions_food[index];
    } else {
        // For default transaction types, check the specific category array
        const specificTransactionArray = resp[transactionType];
        if (specificTransactionArray && Array.isArray(specificTransactionArray) && specificTransactionArray.length > index) {
            currentItem = specificTransactionArray[index];
        }

        // If we didn't find it the direct way, try the long way by reconstructing transactions_default
        if (!currentItem) {
            // Recreate transactions_default from all category-specific arrays
            const typeWithoutPrefix = transactionType.replace('transactions_', '');

            // Reconstruct transactions_default by combining all category arrays
            // except travel and food which have different structures
            const reconstructedTransactionsDefault = [
                ...(Array.isArray(resp.transactions_fundraising) ? resp.transactions_fundraising : []),
                ...(Array.isArray(resp.transactions_medical) ? resp.transactions_medical : []),
                ...(Array.isArray(resp.transactions_medications) ? resp.transactions_medications : []),
                ...(Array.isArray(resp.transactions_insurance) ? resp.transactions_insurance : []),
                ...(Array.isArray(resp.transactions_caregiving) ? resp.transactions_caregiving : []),
                ...(Array.isArray(resp.transactions_physical_therapy) ? resp.transactions_physical_therapy : []),
                ...(Array.isArray(resp.transactions_relocation_lodging) ? resp.transactions_relocation_lodging : []),
                ...(Array.isArray(resp.transactions_other) ? resp.transactions_other : []),
            ];

            // Find transaction with matching category in reconstructed array
            const categoryInfo = categories?.find(
                (c) => c.value === typeWithoutPrefix
            );

            if (categoryInfo && Array.isArray(reconstructedTransactionsDefault)) {
                // Try to find the item by category and possibly index
                if (index < reconstructedTransactionsDefault.length) {
                    // If we have a specific index, try to use it first
                    const possibleItems = reconstructedTransactionsDefault.filter(
                        (t) => t && t.category === categoryInfo.id
                    );
                    currentItem = index < possibleItems.length ? possibleItems[index] : null;
                }

                // If we still haven't found it, try to find any item with this category
                if (!currentItem) {
                    currentItem = reconstructedTransactionsDefault.find(
                        (t) => t && t.category === categoryInfo.id
                    );
                }

                console.log('Found item via category:', currentItem);
            }
        }
    }

    return { currentItem, currentType };
};

/**
 * Process all pending uploads for a fund request item
 */
export const processUploads = async (
    requestId: string | undefined,
    pendingUploads: Record<string, PendingUpload[]>,
    uploadPath: string | null,
    formKey: string | null,
    response: any
): Promise<boolean> => {
    try {

        if (!requestId) {
            console.error('Cannot process uploads: requestId is missing');
            return false;
        }

        if (!formKey) {
            console.error('Cannot process uploads: formKey is missing');
            return false;
        }

        if (!pendingUploads || !pendingUploads[formKey] || pendingUploads[formKey].length === 0) {
            console.log('No pending uploads to process for formKey:', formKey);
            return true; // Successfully completed (no uploads to process)
        }

        // Find the current item and expense type from the response
        const { currentItem, currentType } = findCurrentItem(response, formKey, []);
        const expenseItemType = currentType ? getExpenseItemType(currentType) : null;

        if (!currentItem) {
            console.error('Cannot process uploads: currentItem not found for formKey', formKey);
            return false;
        }

        // Ensure we have a valid upload path, construct one if not provided
        let finalUploadPath = uploadPath;
        if (!finalUploadPath) {
            // Build the path using the format: /v2/fundrequests/{requestId}/expenseitems/{expenseItemType}/{itemId}
            finalUploadPath = `/v2/fundrequests/${requestId}/expenseitems/${expenseItemType}/${currentItem.id}`;
        }

        for (const upload of pendingUploads[formKey]) {
            try {

                // Get presigned URL
                const presignedUrlResponse = await fetchFundRequestItemPresignedUrl(
                    finalUploadPath,
                    encodeURIComponent(upload.originalName)
                );

                if (presignedUrlResponse.error) {
                    console.error('Error getting presigned URL:', presignedUrlResponse.error);
                    continue;
                }

                // Check for S3 direct upload format (url + fields)
                if (presignedUrlResponse.url && presignedUrlResponse.fields) {

                    // Upload file using the S3 direct upload format
                    await uploadFile(
                        presignedUrlResponse.fields,
                        presignedUrlResponse.url,
                        upload.file
                    );

                    // Construct the final image URL
                    const s3Key = presignedUrlResponse.fields.key;
                    // For S3, the url is the bucket URL and should be combined with the key correctly
                    // Make sure we don't have double slashes in the URL
                    const bucketUrl = presignedUrlResponse.url.endsWith('/')
                        ? presignedUrlResponse.url
                        : presignedUrlResponse.url + '/';
                    const imageUrl = `${bucketUrl}${s3Key}`;

                    // Save image reference
                    if (currentItem && currentItem.id) {
                        await saveFundRequestItemImage(
                            finalUploadPath,
                            upload.originalName,
                            imageUrl
                        );
                    } else {
                        console.error('Cannot save image reference: currentItem ID is missing');
                    }
                    continue;
                }

                // Legacy format with presigned_url
                if (!presignedUrlResponse || !presignedUrlResponse.presigned_url) {
                    console.error('Failed to obtain presigned URL for:', upload.originalName);
                    console.error('Response received:', JSON.stringify(presignedUrlResponse));
                    continue;
                }

                // Upload file
                await uploadFile(
                    {}, // Empty fields object
                    presignedUrlResponse.presigned_url,
                    upload.file
                );

                // Save image reference
                if (currentItem && currentItem.id) {
                    await saveFundRequestItemImage(
                        finalUploadPath,
                        upload.originalName,
                        presignedUrlResponse.image_url || `${presignedUrlResponse.presigned_url.url}/${upload.originalName}`
                    );
                } else {
                    console.error('Cannot save image reference: currentItem ID is missing');
                }
            } catch (error) {
                console.error('Error processing upload:', upload.originalName, error);
                // Continue with other uploads instead of failing the entire process
            }
        }

        return true;
    } catch (error) {
        console.error('Error in processUploads:', error);
        return false;
    }
};

/**
 * Save fund request data and process any uploads
 */
export const saveFundRequest = async (
    id: string,
    data: any,
    formKey: string | null,
    pendingUploads: Record<string, PendingUpload[]>,
    categories: SelectOption[],
    clearPendingUploads: (formKey: string) => void,
    options?: {
        onSuccess?: (updatedData: any) => void;
        onError?: (error: any) => void;
    }
) => {

    try {
        // Combine default type transactions
        const { processedData, transactions_default } = combineDefaultTransactions(data);

        // First, save the fund request to get IDs for new expenses
        const resp = await updateFundRequest(
            id,
            Object.assign({}, processedData, { transactions_default })
        );

        // Check if there are pending uploads in the context
        const hasPendingUploads = pendingUploads &&
            Object.keys(pendingUploads).length > 0 &&
            formKey &&
            Array.isArray(pendingUploads[formKey]) &&
            pendingUploads[formKey].length > 0;

        if (hasPendingUploads) {

            try {
                // Get current expense item from the response based on formKey
                const { currentItem, currentType } = findCurrentItem(resp, formKey, categories);
                console.log('Found current item for uploads:', currentItem);
                console.log('Current item type:', currentType);

                // If we found a valid item with an ID, process its uploads
                if (currentItem?.id && currentType) {
                    console.log('Processing uploads for item:', currentItem.id);

                    const uploadsProcessed = await processUploads(
                        resp.id,
                        pendingUploads,
                        null,
                        formKey,
                        resp
                    );

                    if (uploadsProcessed) {
                        console.log('Uploads processed successfully');
                    } else {
                        console.warn('Some uploads may not have been processed successfully');
                    }

                    // Clear the pending uploads regardless of success or failure
                    // This prevents the UI from showing stale uploads
                    clearPendingUploads(formKey);
                } else {

                    // Still clear the pending uploads to prevent issues on subsequent saves
                    clearPendingUploads(formKey);
                }
            } catch (uploadError) {
                console.error('Error processing uploads:', uploadError);
                // Continue with returning the response even if uploads failed
                // We don't want to throw an error here as the main save operation succeeded

                // Clear the pending uploads to prevent UI issues
                clearPendingUploads(formKey);
            }
        } else {
            console.log('No pending uploads to process');
        }

        // Fetch the updated fund request to get the newly uploaded files
        const updatedResp = await fetchFundRequestDetail(id);

        // Call success callback if provided
        if (options?.onSuccess) {
            options.onSuccess(updatedResp);
        }

        return updatedResp;
    } catch (error) {
        console.error('Error in saveFundRequest:', error);

        // Call error callback if provided
        if (options?.onError) {
            console.log('Calling onError callback with error');
            options.onError(error);
        }

        throw error;
    }
};

/**
 * Determines the default payment type based on vendor and payment settings
 */
export const determineDefaultPaymentType = (vendor?: Vendor | null, paymentType?: string): string => {
    if (vendor?.payment_type && !paymentType) {
        return vendor.payment_type;
    } else if (paymentType) {
        return paymentType;
    }
    return '';
};

/**
 * Collects flagged comments from various transaction types
 */
export const collectFlaggedComments = (
    defaultTransactions: DefaultExpenseItem[],
    travelTransactions: TransactionTravel[],
    foodTransactions: TransactionFood[]
): string[] => {
    const flagged: string[] = [];

    // Helper function to safely process each transaction
    const processFlaggedItem = (item: any) => {
        if (item && item.flagged_comment && !item.flagged_resolved_date) {
            flagged.push(item.flagged_comment);
        }
    };

    // Process all transaction types
    defaultTransactions.forEach(processFlaggedItem);
    travelTransactions.forEach(processFlaggedItem);
    foodTransactions.forEach(processFlaggedItem);

    return flagged;
};

/**
 * Combines default transactions from various categories into a single array
 * This version accepts a FundRequest object and combines all default transaction types
 */
export const combineAllDefaultTransactions = (resp: FundRequest): DefaultExpenseItem[] => {
    return [
        resp.transactions_fundraising || [],
        resp.transactions_medical || [],
        resp.transactions_medications || [],
        resp.transactions_caregiving || [],
        resp.transactions_physical_therapy || [],
        resp.transactions_insurance || [],
        resp.transactions_relocation_lodging || [],
        resp.transactions_other || [],
    ].flat();
}; 